/**
 * keybinds.ts — the keyboard's binding table, the same shape the pad's is.
 *
 * Every consumer used to test `e.code === 'KeyW'` in its own module, which is
 * why the keyboard was the one input a player could not change. The codes now
 * live in ONE table and every read goes through it, so a rebind reaches the
 * avatar, the skimmer, the ship and the sector at once.
 *
 * The rules are deliberately the pad's rules (`lib/gamepad.ts`), because a
 * player should not have to learn two:
 *   · a binding is a SET of codes — any of them satisfies the action;
 *   · a code may mean one thing per CONTEXT, and contexts that never share a
 *     frame may share a code (WASD drives on foot AND in a skimmer because you
 *     are only ever doing one of them);
 *   · a rebind that collides is refused and NAMES the clash rather than
 *     silently winning.
 *
 * One thing differs: an action may be left UNBOUND here. Guard and the attack
 * are the mouse's job by default, and forcing a key onto them would be a lie.
 *
 * `KeyboardEvent.code` is the unit, never `key`: `code` is the physical key, so
 * a binding survives a layout change and an AZERTY player's ZQSD lands where
 * their fingers already are.
 */

export type KeyActionName =
  // on foot
  | 'moveForward' | 'moveBack' | 'moveLeft' | 'moveRight'
  | 'jump' | 'sprint' | 'crouch' | 'attack' | 'guard' | 'interact'
  | 'weaponNext' | 'weaponPrev'
  // a skimmer
  | 'throttleUp' | 'throttleDown' | 'steerLeft' | 'steerRight' | 'handbrake' | 'boost'
  // a ship, in atmosphere or the sector
  | 'rollLeft' | 'rollRight' | 'levelOut' | 'fire'
  // the shell, wherever you are
  | 'menu' | 'map' | 'inventory';

/** The defaults — exactly what the code tested before the table existed. */
export const KEY_DEFAULT_BINDINGS: Readonly<Record<KeyActionName, readonly string[]>> = {
  moveForward: ['KeyW', 'ArrowUp'],
  moveBack:    ['KeyS', 'ArrowDown'],
  moveLeft:    ['KeyA', 'ArrowLeft'],
  moveRight:   ['KeyD', 'ArrowRight'],
  jump:        ['Space'],
  sprint:      ['ShiftLeft', 'ShiftRight'],
  crouch:      ['ControlLeft'],
  attack:      ['KeyF'],
  guard:       [],                       // the right mouse button, by default
  interact:    ['KeyE'],
  weaponNext:  ['KeyQ'],
  weaponPrev:  [],                       // Shift+Q, and the wheel
  throttleUp:  ['KeyW', 'ArrowUp'],
  throttleDown:['KeyS', 'ArrowDown'],
  steerLeft:   ['KeyA', 'ArrowLeft'],
  steerRight:  ['KeyD', 'ArrowRight'],
  handbrake:   ['Space'],
  boost:       ['ShiftLeft', 'ShiftRight'],
  rollLeft:    ['KeyA', 'KeyQ'],
  rollRight:   ['KeyD', 'KeyE'],
  levelOut:    ['KeyR'],
  fire:        ['Space'],
  menu:        ['Escape'],
  map:         ['Tab'],
  inventory:   ['F1'],
};

export const KEY_ACTIONS = Object.keys(KEY_DEFAULT_BINDINGS) as KeyActionName[];

/** Which actions can be live at the same moment. A code may serve one action
 *  per context; across contexts that never share a frame it may be reused. */
export const KEY_CONTEXTS: Readonly<Record<string, readonly KeyActionName[]>> = {
  foot: ['moveForward', 'moveBack', 'moveLeft', 'moveRight', 'jump', 'sprint', 'crouch', 'attack', 'guard', 'interact', 'weaponNext', 'weaponPrev', 'menu', 'map', 'inventory'],
  vehicle: ['throttleUp', 'throttleDown', 'steerLeft', 'steerRight', 'handbrake', 'boost', 'menu', 'map', 'inventory'],
  flight: ['throttleUp', 'throttleDown', 'rollLeft', 'rollRight', 'levelOut', 'fire', 'boost', 'menu', 'map', 'inventory'],
};

/** A readable name for a `KeyboardEvent.code`. */
export function keyLabel(code: string): string {
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  if (code.startsWith('Arrow')) return { Up: '↑', Down: '↓', Left: '←', Right: '→' }[code.slice(5)] ?? code;
  if (code.startsWith('Numpad')) return `Num ${code.slice(6)}`;
  const named: Record<string, string> = {
    Space: 'Space', Escape: 'Esc', Tab: 'Tab', Enter: 'Enter', Backspace: '⌫',
    ShiftLeft: 'L Shift', ShiftRight: 'R Shift', ControlLeft: 'L Ctrl', ControlRight: 'R Ctrl',
    AltLeft: 'L Alt', AltRight: 'R Alt', CapsLock: 'Caps', Backquote: '`', Minus: '−', Equal: '=',
    BracketLeft: '[', BracketRight: ']', Backslash: '\\', Semicolon: ';', Quote: "'",
    Comma: ',', Period: '.', Slash: '/',
  };
  return named[code] ?? code;
}

const LS = 'atlas:keybinds';

interface KeyService {
  binds: Record<KeyActionName, string[]>;
  capture: KeyActionName | null;
  last: { action: KeyActionName; code: string | null; conflicts: KeyActionName[] } | null;
  seq: number;
}

function loadBinds(): Record<KeyActionName, string[]> {
  const out = {} as Record<KeyActionName, string[]>;
  for (const a of KEY_ACTIONS) out[a] = [...KEY_DEFAULT_BINDINGS[a]];
  try {
    const raw = localStorage.getItem(LS);
    if (!raw) return out;
    const saved = JSON.parse(raw) as Record<string, string[]>;
    for (const [k, v] of Object.entries(saved)) {
      // An entry naming an action this build no longer has is DROPPED, so an old
      // save can never leave the keyboard half-wired.
      if (!(k in out) || !Array.isArray(v)) continue;
      out[k as KeyActionName] = v.filter((c) => typeof c === 'string' && c.length > 0 && c.length < 24);
    }
  } catch { /* private mode, or a corrupt save — the defaults stand */ }
  return out;
}

const G = globalThis as unknown as { __atlasKeys?: KeyService };
function svc(): KeyService {
  if (!G.__atlasKeys) G.__atlasKeys = { binds: loadBinds(), capture: null, last: null, seq: 0 };
  return G.__atlasKeys;
}

/** The live table (a copy — callers must not mutate the service's own). */
export function keyBindings(): Record<KeyActionName, string[]> {
  const s = svc();
  const out = {} as Record<KeyActionName, string[]>;
  for (const a of KEY_ACTIONS) out[a] = [...s.binds[a]];
  return out;
}

export function keyContextsOf(action: KeyActionName): string[] {
  return Object.entries(KEY_CONTEXTS).filter(([, list]) => list.includes(action)).map(([k]) => k);
}

/** Which actions binding `codes` to `action` would collide with. Empty = legal. */
export function keyConflicts(
  table: Readonly<Record<KeyActionName, readonly string[]>>,
  action: KeyActionName,
  codes: readonly string[],
): KeyActionName[] {
  const mine = new Set(codes);
  const ctxs = new Set(keyContextsOf(action));
  const out: KeyActionName[] = [];
  for (const other of KEY_ACTIONS) {
    if (other === action) continue;
    if (!keyContextsOf(other).some((c) => ctxs.has(c))) continue;
    if (table[other].some((c) => mine.has(c)) && !out.includes(other)) out.push(other);
  }
  return out;
}

/** Rebind. Returns the conflicts on refusal; an EMPTY binding is allowed here
 *  (the mouse covers guard and the attack). */
export function setKeyBinding(action: KeyActionName, codes: readonly string[]): KeyActionName[] {
  const s = svc();
  const clean = codes.filter((c) => typeof c === 'string' && c.length > 0);
  const clash = keyConflicts(s.binds, action, clean);
  if (clash.length) return clash;
  s.binds[action] = [...clean];
  save();
  return [];
}

export function resetKeyBindings(): void {
  const s = svc();
  for (const a of KEY_ACTIONS) s.binds[a] = [...KEY_DEFAULT_BINDINGS[a]];
  try { localStorage.removeItem(LS); } catch { /* private mode */ }
}

function save(): void {
  const s = svc();
  const diff: Record<string, string[]> = {};
  for (const a of KEY_ACTIONS) {
    const d = KEY_DEFAULT_BINDINGS[a];
    if (s.binds[a].length !== d.length || s.binds[a].some((v, i) => v !== d[i])) diff[a] = s.binds[a];
  }
  try {
    if (Object.keys(diff).length) localStorage.setItem(LS, JSON.stringify(diff));
    else localStorage.removeItem(LS);
  } catch { /* private mode */ }
}

/** Is `code` bound to `action`? The one question every consumer asks. */
export function keyIs(code: string, action: KeyActionName): boolean {
  return svc().binds[action].includes(code);
}

/** Is any code bound to `action` currently held, in the caller's own key set? */
export function keyHeldIn(held: ReadonlySet<string>, action: KeyActionName): boolean {
  for (const c of svc().binds[action]) if (held.has(c)) return true;
  return false;
}

/** The readable binding, for a settings row. */
export function keyBindingText(action: KeyActionName): string {
  const b = svc().binds[action];
  return b.length ? b.map(keyLabel).join(' / ') : '—';
}

// ── Capture ──────────────────────────────────────────────────────────────────
// A rebind waits for the next key. It runs in the key handler rather than a poll
// because that is where the browser tells us the code.

export function keyBeginCapture(action: KeyActionName): void { svc().capture = action; }
export function keyCancelCapture(): void { svc().capture = null; }
export function keyCaptureActive(): KeyActionName | null { return svc().capture; }
export function keyCaptureSeq(): number { return svc().seq; }
export function keyLastCapture(): KeyService['last'] { return svc().last; }

/**
 * Offer a pressed code to a pending capture. Returns true when it consumed the
 * press, which is what stops the game also acting on it.
 *
 * Escape CANCELS rather than binds — it is the one key a player always needs to
 * get out with, and a capture that swallowed it could strand them in the prompt.
 */
export function keyCaptureStep(code: string): boolean {
  const s = svc();
  if (!s.capture) return false;
  const action = s.capture;
  if (code === 'Escape') { s.capture = null; s.last = { action, code: null, conflicts: [] }; s.seq++; return true; }
  const conflicts = setKeyBinding(action, [code]);
  s.capture = null;
  s.last = { action, code: conflicts.length ? null : code, conflicts };
  s.seq++;
  return true;
}
